import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import apiClient from '../../utils/api';
import { formatShort, formatRange } from '../twin/format';

// Change the goal for one number. Opens under its card on the You page.
// Two reference ranges to pick from, or type your own low and high. Your
// choice always wins; an out-of-range goal gets a note, never a refusal.

const Pill = ({ on, children, ...props }) => (
  <button
    type="button"
    aria-pressed={!!on}
    className={`rounded-full border px-4 py-2 text-left text-base font-medium transition-colors disabled:opacity-50 ${
      on ? 'border-fg-3 bg-elev text-fg' : 'border-line-strong text-fg-2 hover:border-fg-3 hover:text-fg'
    }`}
    {...props}
  >
    {children}
  </button>
);

const DialTargetEditor = ({ target, onSaved, onRemove }) => {
  const [low, setLow] = useState(target.ultimate_low ?? '');
  const [high, setHigh] = useState(target.ultimate_high ?? '');
  const [custom, setCustom] = useState(!!target.ultimate_is_custom);
  const [showWhy, setShowWhy] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLow(target.ultimate_low ?? '');
    setHigh(target.ultimate_high ?? '');
    setCustom(!!target.ultimate_is_custom);
  }, [target.ultimate_low, target.ultimate_high, target.ultimate_is_custom]);

  const patch = async (body, msg) => {
    setSaving(true);
    try {
      const res = await apiClient.patch(`/api/twin/targets/${target.metric_key}`, body);
      onSaved(res.data);
      if (msg) toast.success(msg);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save that');
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      const res = await apiClient.post(`/api/twin/targets/${target.metric_key}/reset`);
      onSaved(res.data);
      toast.success('Back to the reference range');
    } catch (error) {
      toast.error('Could not reset that');
    } finally {
      setSaving(false);
    }
  };

  const u = target.unit;
  const range = (lo, hi) => formatRange(lo, hi, u) || '—';
  const tierOn = (tier) => !target.ultimate_is_custom && target.seed_tier === tier;

  return (
    <div className="rounded-xl border border-line bg-raised px-5 py-4">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-base font-medium">Goal for {target.label.toLowerCase()}</p>
        {target.annotation && <span className="text-sm text-fg-3">noted</span>}
      </div>

      {target.enabled ? (
        <>
          <div className="mt-3 flex flex-wrap gap-2">
            {target.tiers_coincide ? (
              <Pill on={!target.ultimate_is_custom} disabled={saving || !target.ultimate_is_custom} onClick={reset} title={target.optimal_citation}>
                Reference · {range(target.optimal_low, target.optimal_high)}
              </Pill>
            ) : (
              <>
                <Pill on={tierOn('optimal')} disabled={saving || tierOn('optimal')} title={target.optimal_citation}
                  onClick={() => { setCustom(false); patch({ seed_tier: 'optimal' }, 'Aiming at the top-tier range'); }}>
                  Top tier · {range(target.optimal_low, target.optimal_high)}
                </Pill>
                <Pill on={tierOn('standard')} disabled={saving || tierOn('standard')} title={target.standard_citation}
                  onClick={() => { setCustom(false); patch({ seed_tier: 'standard' }, 'Aiming at the normal range'); }}>
                  Normal · {range(target.standard_low, target.standard_high)}
                </Pill>
              </>
            )}
            <Pill on={custom} onClick={() => setCustom(true)}>
              My own{target.ultimate_is_custom ? ` · ${range(target.ultimate_low, target.ultimate_high)}` : ''}
            </Pill>
          </div>

          {custom && (
            <div className="mt-3 flex flex-wrap items-end gap-3">
              <label className="w-28 text-sm text-fg-3">Low ({u})
                <Input type="number" value={low} onChange={(e) => setLow(e.target.value)}
                  min={target.clamp_low} max={target.clamp_high} step="any" className="mt-1" />
              </label>
              <label className="w-28 text-sm text-fg-3">High ({u})
                <Input type="number" value={high} onChange={(e) => setHigh(e.target.value)}
                  min={target.clamp_low} max={target.clamp_high} step="any" className="mt-1" />
              </label>
              <Button size="sm" disabled={saving} onClick={() => patch({
                ultimate_low: low === '' ? null : parseFloat(low),
                ultimate_high: high === '' ? null : parseFloat(high),
              }, 'Goal saved')}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
              {target.ultimate_is_custom && (
                <Button variant="outline" size="sm" onClick={reset} disabled={saving}>Back to reference</Button>
              )}
            </div>
          )}

          {target.annotation && (
            <p className="mt-3 text-sm text-fg-2">{target.annotation}</p>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <button type="button" onClick={() => setShowWhy((s) => !s)}
              className="bg-transparent p-0 font-medium text-fg-2 hover:text-fg">
              {showWhy ? 'Hide where these come from' : 'Where these ranges come from'}
            </button>
            <button type="button" onClick={() => patch({ enabled: false }, `Stopped tracking ${target.label.toLowerCase()}`)}
              disabled={saving} className="bg-transparent p-0 font-medium text-fg-3 hover:text-fg">
              Stop tracking this
            </button>
            {onRemove && (
              <button type="button" onClick={onRemove} disabled={saving}
                className="bg-transparent p-0 font-medium text-fg-3 hover:text-fg">
                Take off this page
              </button>
            )}
          </div>

          {showWhy && (
            <div className="mt-2 space-y-1.5 text-sm text-fg-3">
              {target.tiers_coincide ? (
                <p><span className="text-fg-2">{range(target.optimal_low, target.optimal_high)}</span> — {target.optimal_citation}</p>
              ) : (
                <>
                  <p><span className="text-fg-2">Top tier {range(target.optimal_low, target.optimal_high)}</span> — {target.optimal_citation}</p>
                  <p><span className="text-fg-2">Normal {range(target.standard_low, target.standard_high)}</span> — {target.standard_citation}</p>
                </>
              )}
              {target.average_citation && (
                <p><span className="text-fg-2">Average person{target.average_value != null ? ` ${formatShort(target.average_value, u)} ${u}` : ''}</span> — {target.average_citation}</p>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <p className="text-base text-fg-3">Not tracking this one.</p>
          <Button size="sm" variant="outline" disabled={saving} onClick={() => patch({ enabled: true }, `Tracking ${target.label.toLowerCase()} again`)}>
            Track it again
          </Button>
          {onRemove && (
            <button type="button" onClick={onRemove} className="bg-transparent p-0 text-sm font-medium text-fg-3 hover:text-fg">
              Take off this page
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default DialTargetEditor;
